import React, { useEffect, useMemo, useState } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  FireIcon,
  BeakerIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  differentialDiagnosisApi,
  SymptomDefinition,
  DifferentialDiagnosisResult,
  RankedDifferential,
} from '../../api/differentialDiagnosis';
import { diseaseWorkflowsApi } from '../../api/diseaseWorkflows';

interface SymptomInvestigationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  birdId: string;
  birdLabel: string;
  initialSymptomIds?: string[];
  initialChecklistItemIds?: string[];
  onWorkflowStarted?: () => void;
}

type Step = 'symptoms' | 'results' | 'protocol';

export default function SymptomInvestigationWizard({
  isOpen,
  onClose,
  birdId,
  birdLabel,
  initialSymptomIds = [],
  initialChecklistItemIds = [],
  onWorkflowStarted,
}: SymptomInvestigationWizardProps) {
  const [step, setStep] = useState<Step>('symptoms');
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [symptoms, setSymptoms] = useState<SymptomDefinition[]>([]);
  const [systemLabels, setSystemLabels] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSymptomIds));
  const [freeText, setFreeText] = useState('');
  const [search, setSearch] = useState('');
  const [activeSystem, setActiveSystem] = useState<string>('ALL');
  const [result, setResult] = useState<DifferentialDiagnosisResult | null>(null);
  const [selectedDifferential, setSelectedDifferential] = useState<RankedDifferential | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep('symptoms');
    setResult(null);
    setSelectedDifferential(null);
    const ids = new Set(initialSymptomIds);
    if (initialChecklistItemIds.length) {
      differentialDiagnosisApi.getCatalog().then((catalog) => {
        for (const itemId of initialChecklistItemIds) {
          for (const sid of catalog.checklistMap[itemId] ?? []) ids.add(sid);
        }
        setSelected(ids);
      });
    } else {
      setSelected(ids);
    }
    loadCatalog();
  }, [isOpen, birdId]);

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const catalog = await differentialDiagnosisApi.getCatalog();
      setSymptoms(catalog.symptoms);
      setSystemLabels(catalog.systemLabels);
    } catch {
      toast.error('Failed to load symptom catalog');
    } finally {
      setLoading(false);
    }
  };

  const systems = useMemo(() => {
    const set = new Set(symptoms.map((s) => s.system));
    return ['ALL', ...Array.from(set)];
  }, [symptoms]);

  const filteredSymptoms = useMemo(() => {
    const q = search.toLowerCase();
    return symptoms.filter((s) => {
      if (activeSystem !== 'ALL' && s.system !== activeSystem) return false;
      if (!q) return true;
      return (
        s.label.toLowerCase().includes(q) ||
        s.synonyms.some((syn) => syn.toLowerCase().includes(q))
      );
    });
  }, [symptoms, search, activeSystem]);

  const toggleSymptom = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runAnalysis = async () => {
    if (selected.size === 0 && !freeText.trim()) {
      toast.error('Select at least one symptom or describe what you observe');
      return;
    }
    try {
      setComputing(true);
      const freeTextSymptoms = freeText
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const data = await differentialDiagnosisApi.compute({
        symptomIds: [...selected],
        freeTextSymptoms,
        checklistItemIds: initialChecklistItemIds,
      });
      setResult(data);
      setSelectedDifferential(data.differentials[0] ?? null);
      setStep('results');
    } catch {
      toast.error('Failed to compute differential diagnosis');
    } finally {
      setComputing(false);
    }
  };

  const startWorkflow = async () => {
    if (!selectedDifferential) return;
    try {
      const symptomLabels = [...selected].map(
        (id) => symptoms.find((s) => s.id === id)?.label ?? id,
      );
      await diseaseWorkflowsApi.triggerWorkflow(birdId, selectedDifferential.diseaseName, symptomLabels);
      toast.success(`Care plan started for ${selectedDifferential.diseaseName}`);
      onWorkflowStarted?.();
      onClose();
    } catch {
      toast.error('Failed to start disease workflow');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay z-50 flex items-center justify-center p-4">
      <div className="modal-shell flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Symptom investigation</h2>
            <p className="text-sm text-slate-500">
              {birdLabel} — observe symptoms first; disease is a ranked hypothesis
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-cyan-600" />
            </div>
          ) : step === 'symptoms' ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-cyan-100 bg-cyan-50/60 p-4 text-sm text-slate-700">
                <strong>Clinical approach:</strong> List what you see (problem-oriented medicine). The system ranks likely diseases and suggests diagnostic steps — it does not replace veterinary diagnosis.
              </div>

              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search symptoms (e.g. breathing, droppings, lethargy)..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input w-full pl-10 pr-3 text-sm"
                />
              </div>

              <div className="flex flex-wrap gap-1">
                {systems.map((sys) => (
                  <button
                    key={sys}
                    type="button"
                    onClick={() => setActiveSystem(sys)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      activeSystem === sys ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {sys === 'ALL' ? 'All systems' : systemLabels[sys] ?? sys}
                  </button>
                ))}
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                {filteredSymptoms.map((symptom) => {
                  const isSelected = selected.has(symptom.id);
                  return (
                    <button
                      key={symptom.id}
                      type="button"
                      onClick={() => toggleSymptom(symptom.id)}
                      className={`rounded-lg border p-3 text-left text-sm transition ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-50 ring-1 ring-cyan-500'
                          : 'border-gray-200 hover:border-cyan-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-slate-900">{symptom.label}</span>
                        {symptom.emergency && <FireIcon className="h-4 w-4 shrink-0 text-red-500" />}
                      </div>
                      <span className="mt-1 block text-xs text-slate-500">{systemLabels[symptom.system]}</span>
                    </button>
                  );
                })}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Additional observations (free text)</label>
                <textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  rows={2}
                  placeholder="e.g. lime-green urates, sitting on cage floor, one-sided weakness"
                  className="input mt-1 w-full text-sm"
                />
              </div>

              <p className="text-xs text-slate-500">{selected.size} symptom{selected.size !== 1 ? 's' : ''} selected</p>
            </div>
          ) : step === 'results' && result ? (
            <div className="space-y-4">
              {result.triage.requiresImmediateVet && (
                <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                  <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-900">Emergency triage</p>
                    <p className="mt-1 text-sm text-red-800">{result.triage.triageMessage}</p>
                    {result.triage.emergencySymptoms.length > 0 && (
                      <ul className="mt-2 list-inside list-disc text-sm text-red-700">
                        {result.triage.emergencySymptoms.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-slate-900">Ranked differentials</h3>
                <p className="text-xs text-slate-500">Select the most likely cause to start a structured care workflow</p>
              </div>

              <div className="space-y-2">
                {result.differentials.length === 0 ? (
                  <p className="text-sm text-slate-500">No strong matches — broaden symptoms or consult a veterinarian.</p>
                ) : (
                  result.differentials.map((diff) => (
                    <button
                      key={diff.diseaseName}
                      type="button"
                      onClick={() => setSelectedDifferential(diff)}
                      className={`w-full rounded-lg border p-4 text-left ${
                        selectedDifferential?.diseaseName === diff.diseaseName
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-gray-200 hover:border-cyan-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{diff.diseaseName}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                          {diff.matchPercentage}% match
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{diff.category} · Severity {diff.severity}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {diff.contagious && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">Contagious</span>
                        )}
                        {diff.zoonotic && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">Zoonotic</span>
                        )}
                      </div>
                      {diff.matchedSymptoms.length > 0 && (
                        <p className="mt-2 text-xs text-slate-600">
                          Matches: {diff.matchedSymptoms.join(', ')}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>

              {selectedDifferential && (
                <div className="rounded-lg border border-gray-200 bg-slate-50 p-4 text-sm">
                  <p className="font-medium text-slate-900">Diagnostic plan for {selectedDifferential.diseaseName}</p>
                  <ul className="mt-2 list-inside list-disc text-slate-700">
                    {selectedDifferential.diagnosticSteps.slice(0, 5).map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                  {selectedDifferential.immediateActions.length > 0 && (
                    <>
                      <p className="mt-3 font-medium text-slate-900">Immediate actions</p>
                      <ul className="mt-1 list-inside list-disc text-slate-700">
                        {selectedDifferential.immediateActions.map((a) => (
                          <li key={a}>{a}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {result.husbandryReview.length > 0 && (
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-900">Husbandry review</p>
                  <ul className="mt-1 list-inside list-disc">
                    {result.husbandryReview.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <div>
            {step === 'results' && (
              <button type="button" onClick={() => setStep('symptoms')} className="btn btn-secondary text-sm">
                <ChevronLeftIcon className="h-4 w-4" />
                Edit symptoms
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
              Cancel
            </button>
            {step === 'symptoms' ? (
              <button type="button" onClick={runAnalysis} disabled={computing} className="btn btn-primary text-sm">
                {computing ? 'Analyzing…' : 'Analyze symptoms'}
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={startWorkflow}
                disabled={!selectedDifferential}
                className="btn btn-primary text-sm"
              >
                <BeakerIcon className="h-4 w-4" />
                Start care workflow
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
